import {
  useId,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx.js';
import { useScrollFade } from './scroll-fade.js';

interface FieldFrame {
  label: string;
  help?: ReactNode;
  error?: string | undefined;
  /** A Notice from a check that ran on this field (FormField README). */
  notice?: ReactNode;
  /** Marks a required field in words, not only with colour. */
  required?: boolean | undefined;
}

function Frame({
  id,
  label,
  help,
  error,
  notice,
  required,
  children,
}: FieldFrame & { id: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="f" htmlFor={id}>
        {label}
      </label>
      {/* Visual only: the control's `required` attribute is what is announced. */}
      {required && (
        <span className="req" aria-hidden="true">
          (required)
        </span>
      )}
      {children}
      {help && (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      )}
      {error && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
      {notice}
    </div>
  );
}

function described(id: string, help: unknown, error: unknown) {
  const ids = [help ? `${id}-help` : '', error ? `${id}-error` : ''].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

export type InputFieldProps = FieldFrame &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'required'> & {
    id?: string;
    /**
     * D60: the unit shown inside the field, at its end ("min", "hours", "AUD").
     * Decorative: the label says the unit too, so screen readers hear it once.
     */
    unit?: string;
  };

export function InputField({
  label,
  help,
  error,
  notice,
  required,
  type = 'text',
  id: given,
  unit,
  ...rest
}: InputFieldProps) {
  const generated = useId();
  const id = given ?? generated;
  const input = (
    <input
      id={id}
      type={type}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={described(id, help, error)}
      {...rest}
    />
  );
  return (
    <Frame id={id} label={label} help={help} error={error} notice={notice} required={required}>
      {unit ? (
        <div className="input-unit">
          {input}
          <span className="unit" aria-hidden="true">
            {unit}
          </span>
        </div>
      ) : (
        input
      )}
    </Frame>
  );
}

/**
 * D60: fields side by side that line up: every label, box and help line starts at
 * the same height across the row, however long one label or help is. Stacks on a phone.
 */
export function FieldRow({ children, columns }: { children: ReactNode; columns?: 2 | 3 }) {
  return <div className={cx('field-row', columns === 3 && 'cols-3')}>{children}</div>;
}

export type SelectFieldProps = FieldFrame &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'required'> & {
    id?: string;
    options: { value: string; label: string }[];
  };

export function SelectField({
  label,
  help,
  error,
  notice,
  required,
  options,
  id: given,
  ...rest
}: SelectFieldProps) {
  const generated = useId();
  const id = given ?? generated;
  return (
    <Frame id={id} label={label} help={help} error={error} notice={notice} required={required}>
      <select
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={described(id, help, error)}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Frame>
  );
}

export type TextareaFieldProps = FieldFrame &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'required'> & { id?: string };

export function TextareaField({
  label,
  help,
  error,
  notice,
  required,
  id: given,
  ...rest
}: TextareaFieldProps) {
  const generated = useId();
  const id = given ?? generated;
  return (
    <Frame id={id} label={label} help={help} error={error} notice={notice} required={required}>
      <textarea
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={described(id, help, error)}
        {...rest}
      />
    </Frame>
  );
}

export function Checkbox({
  label,
  ...rest
}: { label: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export interface RadioOption {
  value: string;
  label: ReactNode;
  help?: ReactNode;
  disabled?: boolean;
}

/** A choice between a few options, all visible (D15). One name, one legend. */
export function RadioGroup({
  legend,
  name,
  value,
  options,
  onChange,
  help,
  error,
  inline,
}: {
  legend: string;
  name: string;
  value: string;
  options: RadioOption[];
  onChange: (value: string) => void;
  help?: ReactNode;
  error?: string | undefined;
  inline?: boolean;
}) {
  const id = useId();
  return (
    <fieldset
      className={cx('radios', inline && 'inline')}
      aria-describedby={described(id, help, error)}
      aria-invalid={error ? true : undefined}
    >
      <legend className="f">{legend}</legend>
      {options.map((o) => (
        <label className="radio" key={o.value}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
          />
          <span>
            {o.label}
            {o.help && <small className="help">{o.help}</small>}
          </span>
        </label>
      ))}
      {help && (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      )}
      {error && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** On or off, applied at once (no save). For settings that need Save, use Checkbox. */
export function Switch({
  label,
  checked,
  onChange,
  help,
  disabled,
  layout = 'start',
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  help?: ReactNode;
  disabled?: boolean;
  /**
   * `end` puts the switch at the right of its label with a rule under the row,
   * the settings-list layout of the canvas (D27). `start` is the compact form.
   */
  layout?: 'start' | 'end';
}) {
  const id = useId();
  return (
    <div className={cx('switch-row', layout === 'end' && 'end')}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-describedby={help ? `${id}-help` : undefined}
        className="switch"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      >
        <span className="knob" aria-hidden="true" />
      </button>
      <label htmlFor={id}>
        {label}
        {help && (
          <small className="help" id={`${id}-help`}>
            {help}
          </small>
        )}
      </label>
    </div>
  );
}

export interface TabItem {
  id: string;
  label: string;
  count?: number;
}

/**
 * Tabs with the WAI-ARIA keyboard pattern: arrows move, Home/End jump. Each tab
 * is also an address in the apps (Tabs README), so selection is controlled.
 * Pass `panelId` and wrap the content in `TabPanel` with the same id so the
 * tab and its panel are linked.
 */
export function Tabs({
  tabs,
  selected,
  onSelect,
  label,
  panelId,
}: {
  tabs: TabItem[];
  selected: string;
  onSelect: (id: string) => void;
  label: string;
  panelId?: string;
}) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((t) => t.id === selected);
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.length
        : event.key === 'ArrowLeft'
          ? (index - 1 + tabs.length) % tabs.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? tabs.length - 1
              : -1;
    const tab = next >= 0 ? tabs[next] : undefined;
    if (!tab) return;
    event.preventDefault();
    onSelect(tab.id);
    (event.currentTarget.querySelector(`[data-tab="${tab.id}"]`) as HTMLElement | null)?.focus();
  };
  const fade = useScrollFade<HTMLDivElement>();
  return (
    <div ref={fade} className="tabs" role="tablist" aria-label={label} onKeyDown={move}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          id={panelId ? `${panelId}-tab-${t.id}` : undefined}
          data-tab={t.id}
          aria-selected={t.id === selected}
          aria-controls={panelId ? `${panelId}-panel` : undefined}
          tabIndex={t.id === selected ? 0 : -1}
          onClick={() => onSelect(t.id)}
        >
          {t.label}
          {t.count ? (
            <span className="count">
              {t.count}
              <span className="sr-only"> open</span>
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** The content of the selected tab, linked back to it. */
export function TabPanel({
  panelId,
  selected,
  children,
  className,
}: {
  panelId: string;
  selected: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      id={`${panelId}-panel`}
      role="tabpanel"
      aria-labelledby={`${panelId}-tab-${selected}`}
      tabIndex={0}
      className={cx('tabpanel', className)}
    >
      {children}
    </div>
  );
}
